import { FC, memo, useState } from 'react'

import { ChevronDownIcon, ChevronRightIcon, IconSizes } from '@inno-ui/icons'
import cn from 'classnames'
import { useTranslation } from 'react-i18next'
import type {
  EventType,
  LogEntry,
  LogLevel,
  LoggerHealth,
  SessionSnapshot
} from 'shared/services/logger/logger.types'
import type { ResolvedFrame } from 'shared/services/logger/stackResolver'

import { formatDate, formatTimestamp } from '../logViewer.utils'
import styles from './LogList.module.scss'

// ─── Class maps ───

const LEVEL_DOT_CLASS: Record<LogLevel, string> = {
  debug: styles.levelDotDebug,
  info: styles.levelDotInfo,
  warn: styles.levelDotWarn,
  error: styles.levelDotError
}
const LEVEL_ROW_CLASS: Record<LogLevel, string> = {
  debug: styles.logRowDebug,
  info: styles.logRowInfo,
  warn: styles.logRowWarn,
  error: styles.logRowError
}
const TYPE_BADGE_CLASS: Record<EventType, string> = {
  error: styles.badgeError,
  navigation: styles.badgeNavigation,
  action: styles.badgeAction,
  auth: styles.badgeAuth,
  storage: styles.badgeStorage,
  system: styles.badgeSystem
}

// ─── Props ───

interface LogListProps {
  entries: LogEntry[]
  totalCount: number
  snapshot: SessionSnapshot | null
  health?: LoggerHealth | null
  isLoading: boolean
  isAnyFilterActive: boolean
  expandedIds: Set<string>
  onToggleExpand: (entry: LogEntry) => void
  resolvedStacks: Map<string, ResolvedFrame[]>
  resolvingIds: Set<string>
}

// ─── Component ───

export const LogList: FC<LogListProps> = memo(
  ({
    entries,
    totalCount,
    snapshot,
    health,
    isLoading,
    isAnyFilterActive,
    expandedIds,
    onToggleExpand,
    resolvedStacks,
    resolvingIds
  }) => {
    const { t } = useTranslation('translation', {
      keyPrefix: 'desktop.log_viewer'
    })

    return (
      <>
        {snapshot && (
          <div className={styles.snapshot}>
            <span className={styles.snapshotItem}>
              <strong>{t('snapshot.session')}</strong>
              {snapshot.sessionId.slice(0, 8)}…
            </span>
            <span className={styles.snapshotItem}>
              <strong>{t('snapshot.os')}</strong>
              {snapshot.os}
            </span>
            <span className={styles.snapshotItem}>
              <strong>{t('snapshot.browser')}</strong>
              {snapshot.browser}
            </span>
            {snapshot.userId != null && (
              <span className={styles.snapshotItem}>
                <strong>{t('snapshot.user')}</strong>
                {snapshot.userId}
              </span>
            )}
            <span className={styles.snapshotItem}>
              <strong>{t('snapshot.date')}</strong>
              {formatDate(snapshot.startedAt)}
            </span>
            <span className={cn(styles.snapshotItem, styles.snapshotCount)}>
              {entries.length} / {totalCount}
            </span>
            {health && (
              <span className={styles.snapshotItem} data-testid='logger-health'>
                <strong>Logger</strong>
                {health.status}
                {health.droppedEntries > 0
                  ? ` · ${health.droppedEntries} dropped`
                  : ''}
              </span>
            )}
          </div>
        )}

        <div className={styles.logList}>
          {isLoading && (
            <div className={styles.emptyState}>{t('list.loading')}</div>
          )}

          {!isLoading && entries.length === 0 && (
            <div className={styles.emptyState}>
              {isAnyFilterActive ? t('list.no_match') : t('list.empty')}
            </div>
          )}

          {entries.map(entry => {
            const isExpanded = expandedIds.has(entry.id)
            const hasPayload =
              Object.keys(entry.payload).length > 0 || entry.error != null
            const rawStack = entry.error?.stack ?? entry.payload.stack
            const hasStack = typeof rawStack === 'string'
            const payloadWithoutStack = hasStack
              ? Object.fromEntries(
                  Object.entries({
                    ...entry.payload,
                    ...(entry.error ? { error: entry.error } : {})
                  }).filter(([k]) => k !== 'stack' && k !== 'resolvedStack')
                )
              : {
                  ...entry.payload,
                  ...(entry.error ? { error: entry.error } : {})
                }

            return (
              <div
                key={entry.id}
                className={cn(styles.logRow, LEVEL_ROW_CLASS[entry.level])}
              >
                <div
                  className={cn(styles.logRowMain, {
                    [styles.logRowClickable]: hasPayload
                  })}
                  onClick={() => hasPayload && onToggleExpand(entry)}
                >
                  <span
                    className={cn(
                      styles.levelDot,
                      LEVEL_DOT_CLASS[entry.level]
                    )}
                  />
                  <span className={styles.logTimestamp}>
                    {formatTimestamp(entry.timestamp)}
                  </span>
                  <span
                    className={cn(
                      styles.typeBadge,
                      TYPE_BADGE_CLASS[entry.type]
                    )}
                  >
                    {entry.category ?? entry.type}
                  </span>
                  <span className={styles.logEvent}>{entry.event}</span>
                  {hasPayload && (
                    <span className={styles.expandIcon}>
                      {isExpanded ? (
                        <ChevronDownIcon
                          size={IconSizes.Size12}
                          color='var(--grey-600)'
                        />
                      ) : (
                        <ChevronRightIcon
                          size={IconSizes.Size12}
                          color='var(--grey-600)'
                        />
                      )}
                    </span>
                  )}
                </div>

                {isExpanded && (
                  <div className={styles.payloadArea}>
                    {hasStack && (
                      <StackTrace
                        rawStack={rawStack as string}
                        frames={
                          resolvedStacks.get(entry.id) ??
                          (entry.payload.resolvedStack as
                            | ResolvedFrame[]
                            | undefined) ??
                          null
                        }
                        isResolving={resolvingIds.has(entry.id)}
                      />
                    )}
                    {Object.keys(payloadWithoutStack).length > 0 && (
                      <pre className={styles.payloadJson}>
                        {JSON.stringify(payloadWithoutStack, null, 2)}
                      </pre>
                    )}
                  </div>
                )}
              </div>
            )
          })}
        </div>
      </>
    )
  }
)

// ─── StackTrace ────────────────────────────────────────────────────────────────

interface StackTraceProps {
  rawStack: string
  frames: ResolvedFrame[] | null
  isResolving: boolean
}

const StackTrace: FC<StackTraceProps> = ({ rawStack, frames, isResolving }) => {
  const { t } = useTranslation('translation', {
    keyPrefix: 'desktop.log_viewer.stack'
  })
  const [showAll, setShowAll] = useState(false)
  const [showRaw, setShowRaw] = useState(false)

  if (isResolving) {
    return (
      <div className={styles.stackTrace}>
        <span className={styles.stackResolving}>{t('resolving')}</span>
      </div>
    )
  }

  if (!frames) {
    return (
      <div className={styles.stackTrace}>
        <pre className={styles.stackRaw}>{rawStack}</pre>
      </div>
    )
  }

  const userFrames = frames.filter(f => f.isUserCode)
  const callerFrame = userFrames[0] ?? null
  const displayFrames = showAll ? frames : userFrames
  const hiddenCount = frames.length - userFrames.length

  return (
    <div className={styles.stackTrace}>
      {callerFrame && (
        <div className={styles.stackCaller}>
          <span className={styles.stackCallerLabel}>{t('called_from')}</span>
          <code className={styles.stackCallerPath}>
            {callerFrame.source}:{callerFrame.line}
            {callerFrame.functionName ? ` (${callerFrame.functionName})` : ''}
          </code>
        </div>
      )}

      {!showRaw ? (
        <>
          <div className={styles.stackFrames}>
            {displayFrames.map((f, i) => (
              <div
                key={i}
                className={cn(styles.stackFrame, {
                  [styles.stackFrameDimmed]: !f.isUserCode
                })}
              >
                <span className={styles.stackFrameAt}>at</span>
                <span className={styles.stackFrameName}>
                  {f.functionName || '<anonymous>'}
                </span>
                {f.source && (
                  <span className={styles.stackFrameSource}>
                    {f.source}:{f.line}
                  </span>
                )}
              </div>
            ))}
          </div>
          <div className={styles.stackActions}>
            {hiddenCount > 0 && (
              <button
                className={styles.stackActionBtn}
                onClick={() => setShowAll(v => !v)}
              >
                {showAll
                  ? t('hide_internals', { count: hiddenCount })
                  : t('show_internals', { count: hiddenCount })}
              </button>
            )}
            <button
              className={styles.stackActionBtn}
              onClick={() => setShowRaw(true)}
            >
              {t('raw')}
            </button>
          </div>
        </>
      ) : (
        <div className={styles.stackRawWrap}>
          <button
            className={styles.stackActionBtn}
            onClick={() => setShowRaw(false)}
          >
            {t('resolved')}
          </button>
          <pre className={styles.stackRaw}>{rawStack}</pre>
        </div>
      )}
    </div>
  )
}
